import { useEffect, useState } from "react";
import { useApolloClient, type ApolloClient } from "@apollo/client";
import { Button, Modal, Select, Spin, message } from "antd";
import {
  bootstrapDesignScopeFromCatalogs,
  listDesignScopeCatalogs,
  type BootstrapFeatureCatalogResult,
  type DesignScopeCatalog
} from "pages/subscription-settings/module-settings/bootstrap-design-scope/apis";

export type ImportScopeItemsFromTemplateModalProps = {
  open: boolean;
  gqlClient?: Pick<ApolloClient<unknown>, "mutate"> | null;
  onClose: () => void;
  onImported?: () => void | Promise<void>;
};

function catalogResultFailed(result: BootstrapFeatureCatalogResult) {
  return Boolean(result.error?.trim());
}

function catalogItemCountLabel(itemCount: number) {
  return `${itemCount} Item${itemCount === 1 ? "" : "s"}`;
}

function CatalogSelectOption({
  name,
  itemCount
}: {
  name: string;
  itemCount: number | null | undefined;
}) {
  return (
    <div className="flex w-full min-w-0 items-center justify-between gap-3">
      <span className="min-w-0 truncate">{name}</span>
      {typeof itemCount === "number" ? (
        <span
          className="inline-flex shrink-0 items-center rounded-full border border-solid border-ciq-gray-300 bg-white px-2 py-0.5 text-xs font-normal text-black text-opacity-50"
          data-testid="catalog-option-item-count"
        >
          {catalogItemCountLabel(itemCount)}
        </span>
      ) : null}
    </div>
  );
}

function BootstrapResultSummary({
  results,
  onBack
}: {
  results: BootstrapFeatureCatalogResult[];
  onBack: () => void;
}) {
  return (
    <div data-testid="design-scope-bootstrap-summary">
      <button
        type="button"
        className="mb-4 flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-ciq-primary-800"
        onClick={onBack}
        data-testid="design-scope-bootstrap-summary-back"
      >
        <span className="font-semibold">Back</span>
      </button>
      <p className="mb-1 text-base font-semibold">Import Summary</p>
      <p className="mb-3 text-xs text-black text-opacity-50">
        Scope item import complete. Review the summary below.
      </p>
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {results.map((result) => (
          <li
            key={result.catalogId}
            className="rounded p-3 text-sm"
            style={{ backgroundColor: "var(--ciq-neutral-25)" }}
            data-testid={`bootstrap-result-${result.catalogId}`}
          >
            <p className="m-0 font-semibold">
              {result.catalogName || result.catalogId}
            </p>
            <p className="m-0 mt-1">Created: {result.created}</p>
            <p className="m-0">Skipped: {result.skipped}</p>
            {result.status ? (
              <p className="m-0">Status: {result.status}</p>
            ) : null}
            {result.error ? (
              <p className="m-0 mt-1 text-red-600">{result.error}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ImportScopeItemsFromTemplateModal({
  open,
  gqlClient,
  onClose,
  onImported
}: ImportScopeItemsFromTemplateModalProps) {
  const apolloClient = useApolloClient();
  const [catalogs, setCatalogs] = useState<DesignScopeCatalog[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<
    BootstrapFeatureCatalogResult[] | null
  >(null);

  const showSummary = Boolean(results);
  // Once lists are picked, only the modal's own buttons close it — a stray
  // click outside (or Esc) must not throw the selection away.
  const dismissible = !importing && selectedCatalogIds.length === 0;

  useEffect(() => {
    if (open) return;
    setSelectedCatalogIds([]);
    setCatalogs([]);
    setImporting(false);
    setResults(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await listDesignScopeCatalogs(apolloClient);
        if (cancelled) return;
        if (!response.success) {
          message.error(
            response.errorMsg || "Failed to load design scope templates."
          );
          setCatalogs([]);
          return;
        }
        const rows = Array.isArray(response.data)
          ? (response.data as DesignScopeCatalog[])
          : [];
        setCatalogs(rows);
      } catch {
        if (!cancelled) {
          message.error("Failed to load design scope templates.");
          setCatalogs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apolloClient, open]);

  const handleClose = () => {
    if (importing) return;
    onClose();
  };

  const handleBackToSelect = () => {
    setResults(null);
  };

  const handleImport = async () => {
    if (!selectedCatalogIds.length || importing || showSummary) return;
    if (!gqlClient) {
      message.error("Project session is not ready. Please try again.");
      return;
    }

    setResults(null);
    setImporting(true);

    try {
      const response = await bootstrapDesignScopeFromCatalogs(
        gqlClient,
        selectedCatalogIds
      );
      if (!response.success) {
        message.error(response.errorMsg || "Import failed. Please try again.");
        return;
      }

      const rows = (response.data ?? []) as BootstrapFeatureCatalogResult[];
      if (rows.some(catalogResultFailed)) {
        setResults(rows);
        await onImported?.();
        return;
      }

      const created = rows.reduce((sum, row) => sum + (row.created || 0), 0);
      message.success(
        `Imported ${created} scope item${created === 1 ? "" : "s"}.`
      );
      await onImported?.();
      onClose();
    } catch {
      message.error("Import failed. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      title="Import Scope Items from Lists"
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      closable={!importing}
      mask={{ closable: dismissible }}
      keyboard={dismissible}
      data-testid="import-scope-from-template-modal"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleClose}
            disabled={importing}
            data-testid="import-scope-from-template-cancel"
          >
            {showSummary ? "Close" : "Cancel"}
          </Button>
          {!importing && !showSummary && (
            <Button
              type="primary"
              disabled={!selectedCatalogIds.length || loading}
              loading={importing}
              onClick={() => void handleImport()}
              data-testid="import-scope-from-template-import"
            >
              Import
            </Button>
          )}
        </div>
      }
    >
      {showSummary && results ? (
        <BootstrapResultSummary results={results} onBack={handleBackToSelect} />
      ) : (
        <>
          {loading ? (
            <div className="flex justify-center py-6">
              <Spin />
            </div>
          ) : (
            <Select
              mode="multiple"
              className="w-full"
              placeholder="Select"
              value={selectedCatalogIds}
              onChange={setSelectedCatalogIds}
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={importing}
              data-testid="import-scope-from-template-select"
              options={catalogs.map((catalog) => ({
                value: catalog.id,
                label: catalog.name,
                itemCount: catalog.itemCount
              }))}
              optionRender={(option) => (
                <CatalogSelectOption
                  name={String(option.label ?? "")}
                  itemCount={
                    (option.data as { itemCount?: number | null } | undefined)
                      ?.itemCount
                  }
                />
              )}
              notFoundContent="No templates available"
            />
          )}

          {importing && (
            <div
              className="mt-6 flex flex-col items-center gap-2"
              data-testid="import-scope-from-template-processing"
            >
              <Spin size="medium" />
              <p className="m-0 text-sm font-semibold">Import in progress</p>
              <p className="m-0 text-center text-xs text-black text-opacity-50">
                Import may take a couple of minutes. Please remain on this page
                until import is complete
              </p>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export default ImportScopeItemsFromTemplateModal;
